<template>
  <div class="my-knowits-container">
    <div class="cards-main-container">
      <!-- Left Navigation Bar -->
      <div class="collections-sidebar" :class="sidebarState">
        <!-- Toggle Buttons -->
        <div class="sidebar-controls">
          <div v-if="sidebarState !== 'collapsed'" class="sidebar-toggle left-arrow" @click="collapseSidebar">
            <i class="el-icon-arrow-left"></i>
          </div>
          <div v-if="sidebarState === 'collapsed'" class="sidebar-toggle right-arrow" @click="collapseSidebar">
            <i class="el-icon-arrow-right"></i>
          </div>
          <div v-if="sidebarState === 'normal'" class="sidebar-toggle right-arrow" @click="expandSidebar">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        
        <div class="sidebar-header">
          <h3 v-if="sidebarState !== 'collapsed'">Card Collections</h3>
          <div v-if="sidebarState !== 'collapsed'" class="sidebar-actions">
            <el-button 
              type="text" 
              icon="el-icon-data-line" 
              @click="showStatisticsDialog"
              size="small"
              title="View Statistics"
            >
              Statistics
            </el-button>
            <el-button 
              type="text" 
              icon="el-icon-plus" 
              @click="showNewCollectionDialog"
              size="small"
            >
              New
            </el-button>
          </div>
        </div>
        <!-- 123 -->
        <div class="collections-list">
          <div
            class="collection-item"
            :class="{ active: selectedCollection === null }"
            @click="selectCollection(null)"
            :title="sidebarState === 'collapsed' ? 'All Cards' : ''"
          >
            <i class="el-icon-files"></i>
            <span v-if="sidebarState !== 'collapsed'">All Cards</span>
            <span v-if="sidebarState !== 'collapsed'" class="count">{{ privateCards.length }}</span>
          </div>

          <div
            class="collection-item"
            :class="{ active: selectedCollection === 'uncategorized' }"
            @click="selectCollection('uncategorized')"
            :title="sidebarState === 'collapsed' ? 'Uncategorized' : ''"
          >
            <i class="el-icon-document"></i>
            <span v-if="sidebarState !== 'collapsed'">Uncategorized</span>
            <span v-if="sidebarState !== 'collapsed'" class="count">{{ uncategorizedCardsCount }}</span>
          </div>

          <div
            class="collection-item starred-collection"
            :class="{ active: selectedCollection === 'starred' }"
            @click="selectCollection('starred')"
            :title="sidebarState === 'collapsed' ? 'Starred Knowits' : ''"
          >
            <i class="el-icon-star-on" style="color: #F7BA2A;"></i>
            <span v-if="sidebarState !== 'collapsed'">Starred Knowits</span>
            <span v-if="sidebarState !== 'collapsed'" class="count">{{ starredCardsCount }}</span>
          </div>

          <div class="divider"></div>
          
          <!-- List View (when not expanded) -->
          <template v-if="sidebarState !== 'expanded'">
            <div
              v-for="collection in cardCollections"
              :key="collection.id"
              class="collection-item"
              :class="{ active: selectedCollection?.id === collection.id }"
              @click="selectCollection(collection)"
              :title="sidebarState === 'collapsed' ? collection.name : ''"
            >
              <div class="collection-item-content">
                <div class="collection-item-header">
                  <i class="el-icon-folder"></i>
                  <span v-if="sidebarState !== 'collapsed'" class="collection-name">{{ collection.name }}</span>
                  <span v-if="sidebarState !== 'collapsed'" class="count">{{ collection.card_count || 0 }}</span>
                  <el-dropdown
                    v-if="sidebarState !== 'collapsed'"
                    trigger="click"
                    @command="handleCollectionCommand($event, collection)"
                    @click.stop
                  >
                    <i class="el-icon-more collection-menu"></i>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="edit">Edit</el-dropdown-item>
                      <el-dropdown-item command="delete" style="color: #F56C6C;">Delete</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>

                <!-- Medal Badge -->
                <div v-if="sidebarState !== 'collapsed' && getMedalInfo(collection.card_count || 0)" class="medal-badge" :style="{ background: getMedalInfo(collection.card_count || 0).color }">
                  <span class="medal-icon">{{ getMedalInfo(collection.card_count || 0).icon }}</span>
                  <span class="medal-text">{{ getMedalInfo(collection.card_count || 0).name }}</span>
                </div>

                <!-- Progress Bar -->
                <div v-if="sidebarState !== 'collapsed' && getMedalProgress(collection.card_count || 0)" class="progress-container">
                  <div class="progress-info">
                    <span class="progress-label">Next: {{ getMedalProgress(collection.card_count || 0).nextMedal }}</span>
                    <span class="progress-count">{{ collection.card_count || 0 }}/{{ getMedalProgress(collection.card_count || 0).nextThreshold }}</span>
                  </div>
                  <el-progress
                    :percentage="getMedalProgress(collection.card_count || 0).percentage"
                    :stroke-width="4"
                    :color="getMedalProgress(collection.card_count || 0).color"
                    :show-text="false"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- Grid View (when expanded) -->
          <template v-else>
            <div class="collections-grid-container">
              <h2 class="grid-title">Manage Card Collections</h2>
              <div class="collections-grid">
                <CardCollectionGridCell
                  v-for="collection in cardCollections"
                  :key="collection.id"
                  :collection="collection"
                  @select="selectCollection"
                  @command="handleGridCommand"
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Cards Content -->
      <div class="cards-content">
        <!-- Search and Filter Bar - Fixed at top -->
        <div class="cards-toolbar">
          <div class="toolbar-left">
            <div class="view-mode-selector">
              <el-radio-group v-model="viewMode" size="small">
                <el-radio-button label="grid">
                  <i class="el-icon-s-grid"></i>
                </el-radio-button>
                <el-radio-button label="list">
                  <i class="el-icon-s-operation"></i>
                </el-radio-button>
              </el-radio-group>
            </div>

            <!-- Batch Selection Controls -->
            <template v-if="viewMode === 'list'">
              <el-button
                v-if="!batchSelectionMode"
                @click="enableBatchSelection"
                size="small"
                icon="el-icon-check"
              >
                Batch Selection
              </el-button>
              <template v-else>
                <el-button
                  @click="disableBatchSelection"
                  size="small"
                >
                  Cancel
                </el-button>
                <el-button
                  v-if="selectedCardIds.length === 0 || selectedCardIds.length < filteredCards.length"
                  @click="selectAll"
                  size="small"
                  type="primary"
                >
                  Select All
                </el-button>
                <el-button
                  v-if="selectedCardIds.length > 0"
                  @click="deselectAll"
                  size="small"
                  type="default"
                >
                  Deselect All
                </el-button>
                <span class="selected-count">{{ selectedCardIds.length }} selected</span>
              </template>
            </template>

            <el-input
              v-model="searchQuery"
              placeholder="Search cards..."
              prefix-icon="el-icon-search"
              size="small"
              clearable
              class="search-input"
            />
          </div>
          <div class="toolbar-center">
            <div v-if="selectedCollection && selectedCollection !== 'uncategorized' && selectedCollection !== 'starred'" class="collection-title-display">
              <i class="el-icon-folder"></i>
              <span class="collection-name">{{ selectedCollection.name }}</span>
              <span class="collection-count">{{ filteredCards.length }} cards</span>
            </div>
            <div v-else-if="selectedCollection === 'uncategorized'" class="collection-title-display">
              <i class="el-icon-document"></i>
              <span class="collection-name">Uncategorized</span>
              <span class="collection-count">{{ filteredCards.length }} cards</span>
            </div>
            <div v-else-if="selectedCollection === 'starred'" class="collection-title-display">
              <i class="el-icon-star-on" style="color: #F7BA2A;"></i>
              <span class="collection-name">Starred Knowits</span>
              <span class="collection-count">{{ filteredCards.length }} cards</span>
            </div>
          </div>
          <div class="toolbar-right">
            <!-- Import cards button - only show when a collection is selected -->
            <el-button
              v-if="selectedCollection && selectedCollection !== 'uncategorized'"
              type="warning"
              @click="showImportCardsDialog"
              icon="el-icon-upload2"
              size="small"
            >
              Import cards to this collection
            </el-button>
            <div
              class="new-card-button-wrapper"
              @mouseenter="showCollectionButton = true"
              @mouseleave="showCollectionButton = false"
            >
              <!-- New Card button (hidden) -->
              <el-button type="primary" @click="showAddCardDialog" icon="el-icon-plus" size="small">
                New Card
              </el-button>
              <!-- Add a card in this Collection button - shows on hover -->
              <div
                v-if="selectedCollection && selectedCollection !== 'uncategorized'"
                class="add-in-collection-dropdown"
              >
                <el-button
                  v-show="showCollectionButton"
                  type="success"
                  @click="showAddCardInCollectionDialog"
                  icon="el-icon-folder-add"
                  size="small"
                  class="add-in-collection-button"
                >
                  Add a card in this Collection
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Operation Button Bar (shown when cards are selected) -->
        <div v-if="batchSelectionMode && selectedCardIds.length > 0" class="operation-bar">
          <el-button
            @click="batchAddToContext"
            size="small"
            type="primary"
            icon="el-icon-circle-plus"
          >
            Add to Context
          </el-button>
          <el-button
            @click="batchDelete"
            size="small"
            type="danger"
            icon="el-icon-delete"
          >
            Delete
          </el-button>
          <el-button
            @click="batchChangeCollections"
            size="small"
            icon="el-icon-folder"
          >
            Change Collections
          </el-button>
        </div>

        <!-- Scrollable content area -->
        <div class="cards-scrollable-content">
          <el-skeleton :rows="3" animated v-if="privateCardsLoading" />
      
      <template v-else>
        <!-- List View (Email-style) -->
        <div v-if="viewMode === 'list'" class="cards-list-view">
          <CardListItem
            v-for="card in filteredCards"
            :key="card.id || Math.random()"
            :card="card"
            :collections="cardCollections"
            :can-share="true"
            :is-in-context-pot="isCardInContextPot(card)"
            :batch-selection-mode="batchSelectionMode"
            :is-selected="isCardSelected(card.id)"
            @click="handleCardClick"
            @edit="editCard"
            @delete="deleteCard"
            @delete-success="handleDeleteSuccess"
            @share="handleShareCard"
            @add-to-context="addToContextPot"
            @toggle-selection="toggleCardSelection"
            @toggle-star="toggleCardStar"
          />
        </div>

        <!-- Grid View -->
        <div v-else class="cards-grid">
          <SimpleCardView
            v-for="card in filteredCards"
            :key="card.id || Math.random()"
            :card="card"
            :collections="cardCollections"
            :can-share="true"
            :is-in-context-pot="isCardInContextPot(card)"
            @edit="editCard"
            @delete="deleteCard"
            @delete-success="handleDeleteSuccess"
            @share="handleShareCard"
            @add-to-context="addToContextPot"
            @toggle-star="toggleCardStar"
            @click="openReadCardDialog"
          />
        </div>

        <!-- Empty State -->
        <div v-if="filteredCards.length === 0" class="empty-state">
          <i class="el-icon-document" style="font-size: 48px; color: #909399; margin-bottom: 16px;"></i>
          <template v-if="searchQuery">
            <p>No search results found.</p>
            <p style="font-size: 14px; color: #909399; margin-bottom: 20px;">
              Try adjusting your search terms or clear the search to see all cards.
            </p>
          </template>
          <template v-else>
            <p>You haven't created any Knowit Cards yet.</p>
            <p style="font-size: 14px; color: #909399; margin-bottom: 20px;">
              Knowit Cards are your personal knowledge snippets that you can organize, share, and reference.
            </p>
          </template>
        </div>
      </template>
        </div>
      </div>
    </div>

    <!-- New Collection Dialog -->
    <el-dialog
      :title="editingCollection ? 'Edit Collection' : 'New Card Collection'"
      :visible.sync="collectionDialogVisible"
      width="70%"
      :close-on-click-modal="false"
    >
      <el-form :model="collectionForm" :rules="collectionRules" ref="collectionForm">
        <el-form-item label="Name" prop="name">
          <el-input 
            v-model="collectionForm.name" 
            placeholder="Enter collection name"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="Description" prop="description">
          <el-input 
            type="textarea" 
            v-model="collectionForm.description" 
            placeholder="Enter collection description (optional)"
            rows="3"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="collectionDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="saveCollection" :loading="savingCollection">
          {{ editingCollection ? 'Update' : 'Create' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- Share Card Dialog -->
    <el-dialog
      title="Share Knowit Card"
      :visible.sync="shareCardDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="shareCardData" class="share-card-preview">
        <h4>Card to Share:</h4>
        <div class="card-preview">
          <div class="card-title">{{ shareCardData.title || 'Untitled' }}</div>
          <div class="card-content">{{ truncateText(shareCardData.content, 100) }}</div>
        </div>
      </div>
      
      <el-tabs v-model="shareActiveTab" class="share-tabs">
        <el-tab-pane label="Share by Email" name="email">
          <el-form :model="shareForm" :rules="shareRules" ref="shareForm" label-width="100px">
            <el-form-item label="Recipient" prop="recipient_email">
              <el-input 
                v-model="shareForm.recipient_email" 
                placeholder="Enter recipient's email address"
                @blur="validateRecipient"
              >
              </el-input>
              <div v-if="recipientInfo" class="recipient-info">
                <i class="el-icon-user"></i>
                <span>{{ recipientInfo.user_name || recipientInfo.email }}</span>
              </div>
            </el-form-item>
            <el-form-item label="Message" prop="message">
              <el-input 
                type="textarea" 
                v-model="shareForm.message" 
                placeholder="Add a personal message (optional)"
                rows="3"
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        
        <el-tab-pane label="Share by Image" name="image">
          <div class="share-image-container">
            <div class="share-image-preview" ref="shareImagePreview" v-if="shareCardData">
              <div class="share-image-card">
                <div class="share-image-header">
                  <div class="share-image-logo">
                    <i class="el-icon-document"></i>
                    <span>Knowit Card</span>
                  </div>
                  <div class="share-image-date">{{ formatShareDate(shareCardData.created_at) }}</div>
                </div>
                <div class="share-image-body">
                  <h2 class="share-image-title">{{ shareCardData.title || 'Untitled' }}</h2>
                  <div class="share-image-labels" v-if="shareCardData.labels && shareCardData.labels.length > 0">
                    <span v-for="(label, index) in shareCardData.labels" :key="index" class="share-image-label">
                      {{ label }}
                    </span>
                  </div>
                  <div class="share-image-content">{{ shareCardData.content }}</div>
                  <div class="share-image-resources" v-if="shareCardData.resources && shareCardData.resources.length > 0">
                    <div class="share-image-resources-title">
                      <i class="el-icon-notebook-2"></i>
                      Resources
                    </div>
                    <div v-for="(resource, index) in shareCardData.resources" :key="index" class="share-image-resource">
                      [{{ index + 1 }}] {{ resource.name }}
                      <span v-if="resource.author"> - {{ resource.author }}</span>
                    </div>
                  </div>
                </div>
                <div class="share-image-footer">
                  <div class="share-image-author">
                    <i class="el-icon-user"></i>
                    <span>{{ $store.state.user_info.user_name || 'Anonymous' }}</span>
                  </div>
                  <div class="share-image-app">
                    <span class="app-name">Knowit</span>
                    <span class="app-domain">knowit.today</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="share-image-actions">
              <el-button
                type="primary"
                @click="copyShareImage"
                :loading="generatingImage"
                icon="el-icon-document-copy"
              >
                Copy the image
              </el-button>
              <el-button
                type="success"
                @click="downloadShareImage"
                :loading="generatingImage"
                icon="el-icon-download"
              >
                Download the image
              </el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <!-- Share History Section -->
      <div class="share-history-section" v-if="shareActiveTab === 'email'">
        <div class="share-history-header">
          <h4>Share History</h4>
          <el-button type="text" size="small" @click="fetchShareHistory" icon="el-icon-refresh" :loading="loadingShareHistory">
            Refresh
          </el-button>
        </div>

        <div v-if="loadingShareHistory" class="share-history-loading">
          <i class="el-icon-loading"></i>
          <p>Loading share history...</p>
        </div>

        <div v-else-if="shareHistory && shareHistory.length > 0" class="share-history-list">
          <div v-for="share in shareHistory" :key="share.share_id" class="share-history-item">
            <div class="share-history-info">
              <div class="share-history-recipient">
                <i class="el-icon-user"></i>
                <span>{{ share.recipient_name || share.recipient_email }}</span>
              </div>
              <div class="share-history-date">{{ formatShareDate(share.shared_at) }}</div>
            </div>
            <div class="share-history-status">
              <el-tag :type="share.status === 'sent' ? 'success' : 'info'" size="small">
                {{ share.status }}
              </el-tag>
            </div>
          </div>
        </div>

        <div v-else class="share-history-empty">
          <i class="el-icon-document"></i>
          <p>No share history yet</p>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="shareCardDialogVisible = false">Cancel</el-button>
        <el-button
          v-if="shareActiveTab === 'email'"
          type="primary"
          @click="confirmShareCard"
          :loading="sharingCard"
        >
          Share by Email
        </el-button>
      </div>
    </el-dialog>

    <!-- Login Dialog -->
    <AuthDialog 
      :visible.sync="loginDialogVisible"
      :initial-mode="'login'"
      @login-success="handleLoginSuccess"
      @update:visible="handleAuthDialogClose"
    />

    <!-- AI Dialog Drawer -->
    <el-drawer
      :visible.sync="aiDialogVisible"
      direction="rtl"
      size="80%"
      :with-header="false"
      :modal="false"
      class="ai-drawer"
    >
      <UserAIDialogue 
        :visible="aiDialogVisible"
        @update:visible="aiDialogVisible = $event"
        :context_pot="globalContextPot"
        task-type="chat"
        title="AI Assistant"
      />
    </el-drawer>
    
    <!-- Guide AI Dialog Drawer -->
    <el-drawer
      :visible.sync="guideAIDialogVisible"
      direction="rtl"
      size="80%"
      :with-header="false"
      :modal="false"
      class="guide-ai-drawer"
    >
      <GuideAIDialog 
        :visible="guideAIDialogVisible"
        @update:visible="guideAIDialogVisible = $event"
      />
    </el-drawer>

    <!-- Mark Button -->
    <div class="mark-button" @click="toggleAIDialog" :title="aiDialogVisible ? 'Close AI Assistant (Cmd+L)' : 'Open AI Assistant (Cmd+L)'">
      <i :class="aiDialogVisible ? 'el-icon-close' : 'el-icon-chat-line-square'"></i>
    </div>
    
    <!-- Guide AI Button -->
    <div class="guide-button" @click="toggleGuideAIDialog" :title="guideAIDialogVisible ? 'Close Guide AI' : 'Open Guide AI (Deep Exploration)'">
      <i :class="guideAIDialogVisible ? 'el-icon-close' : 'el-icon-chat-dot-round'"></i>
    </div>
    
    <!-- Import Cards Dialog -->
    <el-dialog
      title="Import Cards to Collection"
      :visible.sync="importCardsDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      class="import-cards-dialog"
    >
      <div v-if="importStep === 'input'" class="import-step-input">
        <!-- Markdown Rules Note -->
        <el-alert
          title="Markdown Import Rules"
          type="info"
          :closable="false"
          class="markdown-rules-alert"
        >
          <div class="markdown-rules">
            <p><strong>How to format your markdown:</strong></p>
            <ul>
              <li>Use <code># Title</code> or <code>## Title</code> to mark card titles</li>
              <li>All content after a title becomes the card content</li>
              <li>Each new title creates a new card</li>
              <li>Content can include regular text, lists, code blocks, etc.</li>
            </ul>
            <p><strong>Example:</strong></p>
            <pre># My First Card
This is the content of my first card.
It can span multiple lines.

# My Second Card
This is another card with different content.</pre>
          </div>
        </el-alert>

        <!-- Text Editor -->
        <el-input
          v-model="importMarkdownText"
          type="textarea"
          :rows="15"
          placeholder="Paste your markdown content here..."
          class="import-textarea"
        />
      </div>

      <div v-else-if="importStep === 'preview'" class="import-step-preview">
        <div class="preview-header">
          <h3>Preview Cards ({{ parsedCards.length }} cards)</h3>
          <el-button size="small" type="text" @click="importStep = 'input'">
            <i class="el-icon-back"></i> Back to Edit
          </el-button>
        </div>

        <div class="preview-cards-list">
          <div
            v-for="(card, index) in parsedCards"
            :key="index"
            class="preview-card-item"
          >
            <div class="preview-card-header">
              <span class="preview-card-index">{{ index + 1 }}</span>
              <h4 class="preview-card-title">{{ card.title || 'Untitled' }}</h4>
            </div>
            <div class="preview-card-content">{{ card.content }}</div>
          </div>
        </div>

        <div class="import-progress" v-if="importing">
          <el-progress
            :percentage="importProgress"
            :status="importProgress === 100 ? 'success' : ''"
          />
          <p class="import-status-text">
            {{ importStatusText }}
          </p>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="importCardsDialogVisible = false" :disabled="importing">Cancel</el-button>
        <el-button
          v-if="importStep === 'input'"
          type="primary"
          @click="parseMarkdownAndPreview"
          :disabled="!importMarkdownText.trim()"
        >
          Parse & Preview
        </el-button>
        <el-button
          v-if="importStep === 'preview'"
          type="success"
          @click="confirmImport"
          :loading="importing"
          :disabled="parsedCards.length === 0"
        >
          {{ importing ? 'Importing...' : 'Confirm to Import' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- Change Collections Dialog -->
    <el-dialog
      title="Change Collections"
      :visible.sync="changeCollectionsDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="change-collections-content">
        <p class="dialog-hint">Select collections for the {{ selectedCardIds.length }} selected card(s):</p>
        <el-checkbox-group v-model="selectedCollectionsForBatch">
          <div
            v-for="collection in cardCollections"
            :key="collection.id"
            class="collection-checkbox-item"
          >
            <el-checkbox :label="collection.id">
              <div class="collection-checkbox-content">
                <i class="el-icon-folder"></i>
                <span class="collection-checkbox-name">{{ collection.name }}</span>
                <span class="collection-checkbox-count">({{ collection.card_count || 0 }} cards)</span>
              </div>
            </el-checkbox>
          </div>
        </el-checkbox-group>
        <div v-if="cardCollections.length === 0" class="no-collections-hint">
          <i class="el-icon-warning"></i>
          <p>No collections available. Please create a collection first.</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="changeCollectionsDialogVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          @click="confirmBatchChangeCollections"
          :disabled="selectedCollectionsForBatch.length === 0"
        >
          Confirm
        </el-button>
      </div>
    </el-dialog>

    <!-- Add/Edit Card Dialog -->
    <AddKnowitDialog
      ref="addKnowitDialog"
      :visible.sync="addCardDialogVisible"
      :card="editingCard"
      :collections="cardCollections"
      :preselectedCollectionId="preselectedCollectionId"
      @saved="handleCardSaved"
      @close="handleDialogClose"
    />

    <!-- Statistics Dialog -->
    <CardStatisticsDialog
      :visible.sync="statisticsDialogVisible"
    />

    <!-- Card Read View Dialog -->
    <el-dialog
      :visible.sync="readCardDialogVisible"
      width="850px"
      :show-close="true"
      custom-class="card-read-dialog"
      @close="readCardDialogVisible = false; selectedCardForReading = null"
    >
      <DirectReadCard
        v-if="selectedCardForReading"
        :card="selectedCardForReading"
        :collections="cardCollections"
        :showAnswer="true"
        :showReminder="true"
      />
    </el-dialog>
  </div>
</template>

<script>
import KnowitCardList from '@/components/KnowitCardList.vue'
import SimpleCardView from '@/components/SimpleCardView.vue'
import DirectReadCard from '@/components/DirectReadCard.vue'
import CardListItem from '@/components/CardListItem.vue'
import CardCollectionGridCell from '@/components/CardCollectionGridCell.vue'
import AuthDialog from '@/components/AuthDialog.vue'
import UserAIDialogue from '@/components/UserAIDialogue.vue'
import GuideAIDialog from '@/components/GuideAIDialog.vue'
import AddKnowitDialog from '@/components/AddKnowitDialog.vue'
import CardStatisticsDialog from '@/components/CardStatisticsDialog.vue'
import { postRequest } from '@/apis/front'
import { getUserId } from '@/utils/store'
import html2canvas from 'html2canvas'

export default {
  name: 'MyKnowits',
  components: {
    KnowitCardList,
    SimpleCardView,
    DirectReadCard,
    CardListItem,
    CardCollectionGridCell,
    AuthDialog,
    UserAIDialogue,
    GuideAIDialog,
    AddKnowitDialog,
    CardStatisticsDialog
  },
  data() {
    return {
      privateCards: [],
      privateCardsLoading: false,
      viewMode: localStorage.getItem('knowitCardsViewMode') || 'grid',
      searchQuery: '',
      loginDialogVisible: false,
      aiDialogVisible: false,
      guideAIDialogVisible: false,
      // Add Card Dialog
      addCardDialogVisible: false,
      editingCard: null,
      preselectedCollectionId: null, // For pre-selecting collection when creating card
      // Sidebar state: 'collapsed' (thin), 'normal' (default), 'expanded' (100%)
      sidebarState: 'normal',
      // Card Collections
      cardCollections: [],
      selectedCollection: null,
      collectionDialogVisible: false,
      editingCollection: null,
      savingCollection: false,
      collectionForm: {
        name: '',
        description: ''
      },
      collectionRules: {
        name: [
          { required: true, message: 'Please enter collection name', trigger: 'blur' },
          { min: 1, max: 50, message: 'Name must be between 1 and 50 characters', trigger: 'blur' }
        ]
      },
      // Share card data
      shareCardDialogVisible: false,
      shareCardData: null,
      sharingCard: false,
      recipientInfo: null,
      shareForm: {
        recipient_email: '',
        message: ''
      },
      shareRules: {
        recipient_email: [
          { required: true, message: 'Please enter recipient email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' }
        ]
      },
      // Statistics dialog
      statisticsDialogVisible: false,
      // Read card dialog
      readCardDialogVisible: false,
      selectedCardForReading: null,
      // Share image tab
      shareActiveTab: 'email',
      generatingImage: false,
      shareHistory: [],
      loadingShareHistory: false,
      // Show collection button on hover
      showCollectionButton: false,
      // Import cards
      importCardsDialogVisible: false,
      importStep: 'input', // 'input' or 'preview'
      importMarkdownText: '',
      parsedCards: [],
      importing: false,
      importProgress: 0,
      importStatusText: '',
      // Batch selection
      batchSelectionMode: false,
      selectedCardIds: [],
      // Change collections dialog
      changeCollectionsDialogVisible: false,
      selectedCollectionsForBatch: []
    }
  },
  computed: {
    globalContextPot() {
      return this.$store.state.contextPot.items;
    },
    filteredCards() {
      let cards = this.privateCards;

      // Filter by collection
      if (this.selectedCollection === 'uncategorized') {
        // Show cards with no collections (support both old and new format)
        cards = cards.filter(card => {
          const hasNoCollectionId = !card.collection_id
          const hasNoCollectionIds = !card.collection_ids || card.collection_ids.length === 0
          return hasNoCollectionId && hasNoCollectionIds
        });
      } else if (this.selectedCollection === 'starred') {
        // Show starred cards only
        cards = cards.filter(card => card.is_starred);
      } else if (this.selectedCollection && this.selectedCollection.id) {
        // Show cards that belong to the selected collection
        cards = cards.filter(card => {
          // Support both old format (collection_id) and new format (collection_ids)
          if (card.collection_ids && Array.isArray(card.collection_ids)) {
            return card.collection_ids.includes(this.selectedCollection.id)
          } else if (card.collection_id) {
            return card.collection_id === this.selectedCollection.id
          }
          return false
        });
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        cards = cards.filter(card => {
          const title = (card.title || '').toLowerCase();
          const content = (card.content || '').toLowerCase();
          const tags = (card.tags || []).join(' ').toLowerCase();
          return title.includes(query) || content.includes(query) || tags.includes(query);
        });
      }

      return cards;
    },
    uncategorizedCardsCount() {
      return this.privateCards.filter(card => {
        const hasNoCollectionId = !card.collection_id
        const hasNoCollectionIds = !card.collection_ids || card.collection_ids.length === 0
        return hasNoCollectionId && hasNoCollectionIds
      }).length;
    },
    starredCardsCount() {
      return this.privateCards.filter(card => card.is_starred).length;
    }
  },
  watch: {
    viewMode(newMode) {
      localStorage.setItem('knowitCardsViewMode', newMode)
    }
  },
  mounted() {
    this.fetchPrivateCards()
    this.fetchCardCollections()
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    openReadCardDialog(card) {
      console.log('Opening read dialog for card:', card)
      console.log('Dialog visible before:', this.readCardDialogVisible)
      this.selectedCardForReading = card
      this.readCardDialogVisible = true
      console.log('Dialog visible after:', this.readCardDialogVisible)
      console.log('Selected card:', this.selectedCardForReading)
      this.$nextTick(() => {
        console.log('Dialog visible in nextTick:', this.readCardDialogVisible)
      })
    },

    handleCardSaved(savedCard) {
      console.log('[MyKnowitCards] handleCardSaved handler invoked')
      try {
        console.log('[MyKnowitCards] handleCardSaved received:', savedCard)
        if (!savedCard) return
        const savedId = savedCard.id != null ? String(savedCard.id) : ''
        console.log('[MyKnowitCards] Normalized savedId =', savedId)
        // Update or insert card locally without full reload
        const index = this.privateCards.findIndex(c => String(c.id) === savedId)
        console.log('[MyKnowitCards] Existing index =', index)
        if (index > -1) {
          const merged = { ...this.privateCards[index], ...savedCard }
          this.$set(this.privateCards, index, merged)
          console.log('[MyKnowitCards] Card updated at index', index, merged)
        } else {
          this.privateCards.unshift(savedCard)
          console.log('[MyKnowitCards] Card inserted at top', savedCard)
        }
        // Update collection counts based on current cards
        console.log('[MyKnowitCards] Updating collection counts...')
        this.updateCollectionCounts()
      } finally {
        // Close dialog and reset saving state on child
        console.log('[MyKnowitCards] Closing dialog...')
        this.addCardDialogVisible = false
        this.preselectedCollectionId = null // Clear preselection after saving
        if (this.$refs.addKnowitDialog) {
          console.log('[MyKnowitCards] Resetting child saving state')
          this.$refs.addKnowitDialog.resetSavingState()
        }
      }
    },
    // Card Collections Methods
    async fetchCardCollections() {
      try {
        const response = await postRequest('getCardCollections', {
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.cardCollections = response.data.data.map(item=>{
            item.id = item._id.$oid || (item._id.$oid || item._id.$id) || item._id
            return item
          }) || []

          // Sort collections: pinned first, then by update time
          this.cardCollections.sort((a, b) => {
            // Pinned collections come first
            if (a.is_pinned && !b.is_pinned) return -1
            if (!a.is_pinned && b.is_pinned) return 1
            // If both pinned or both not pinned, sort by update time (newest first)
            return (b.update_time || 0) - (a.update_time || 0)
          })

          // Don't update counts here - backend already provides accurate card_count
          // this.updateCollectionCounts()
        } else {
          console.error('Failed to fetch card collections:', response.data.msg)
        }
      } catch (error) {
        console.error('Error fetching card collections:', error)
      }
    },

    updateCollectionCounts() {
      // Calculate accurate card counts for each collection
      this.cardCollections.forEach(collection => {
        const count = this.privateCards.filter(card => {
          // Support both old format (collection_id) and new format (collection_ids)
          if (card.collection_ids && Array.isArray(card.collection_ids)) {
            return card.collection_ids.includes(collection.id)
          } else if (card.collection_id) {
            return card.collection_id === collection.id
          }
          return false
        }).length
        collection.card_count = count
      })
    },

    selectCollection(collection) {
      this.selectedCollection = collection
      // Collapse sidebar if expanded when selecting a collection
      if (this.sidebarState === 'expanded') {
        this.sidebarState = 'normal'
      }
    },

    collapseSidebar() {
      // Collapse level by level: expanded -> normal -> collapsed
      if (this.sidebarState === 'expanded') {
        this.sidebarState = 'normal'
      } else if (this.sidebarState === 'normal') {
        this.sidebarState = 'collapsed'
      } else if (this.sidebarState === 'collapsed') {
        this.sidebarState = 'normal'
      }
    },

    expandSidebar() {
      // Expand from normal to expanded
      this.sidebarState = 'expanded'
    },

    handleGridCommand({ command, collection }) {
      this.handleCollectionCommand(command, collection)
    },

    showNewCollectionDialog() {
      this.editingCollection = null
      this.collectionForm = {
        name: '',
        description: ''
      }
      this.collectionDialogVisible = true
    },

    handleCollectionCommand(command, collection) {
      if (command === 'edit') {
        this.editingCollection = collection
        this.collectionForm = {
          name: collection.name,
          description: collection.description || ''
        }
        this.collectionDialogVisible = true
      } else if (command === 'delete') {
        this.deleteCollection(collection)
      }
    },

    async saveCollection() {
      this.$refs.collectionForm.validate(async (valid) => {
        if (!valid) return

        this.savingCollection = true
        try {
          const params = {
            user_id: this.$store.state.user_id,
            name: this.collectionForm.name,
            description: this.collectionForm.description
          }

          if (this.editingCollection) {
            params.collection_id = this.editingCollection.id
          }

          const response = await postRequest(
            this.editingCollection ? 'updateCardCollection' : 'createCardCollection',
            params
          )

          if (response.data.code === 0) {
            this.$message.success(
              this.editingCollection ? 'Collection updated successfully' : 'Collection created successfully'
            )
            this.collectionDialogVisible = false
            await this.fetchCardCollections()
            await this.fetchPrivateCards() // Refresh cards to update counts
          } else {
            this.$message.error(response.data.msg || 'Failed to save collection')
          }
        } catch (error) {
          console.error('Error saving collection:', error)
          this.$message.error('Failed to save collection')
        } finally {
          this.savingCollection = false
        }
      })
    },

    async deleteCollection(collection) {
      this.$confirm(
        `Are you sure you want to delete "${collection.name}"? Cards in this collection will become uncategorized.`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await postRequest('deleteCardCollection', {
            user_id: this.$store.state.user_id,
            collection_id: collection.id
          })

          if (response.data.code === 0) {
            this.$message.success('Collection deleted successfully')
            if (this.selectedCollection?.id === collection.id) {
              this.selectedCollection = null
            }
            await this.fetchCardCollections()
            await this.fetchPrivateCards()
          } else {
            this.$message.error(response.data.msg || 'Failed to delete collection')
          }
        } catch (error) {
          console.error('Error deleting collection:', error)
          this.$message.error('Failed to delete collection')
        }
      }).catch(() => {
        // User cancelled
      })
    },

    async fetchPrivateCards() {
      this.privateCardsLoading = true
      try {
        const response = await postRequest('getMyCards', {
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.privateCards = response.data.data || []
          // Update collection counts after cards are loaded
          this.updateCollectionCounts()
        } else {
          console.error('Failed to fetch private cards:', response.data.msg)
        }
      } catch (error) {
        console.error('Error fetching private cards:', error)
        this.$message.error('Failed to load private cards')
      } finally {
        this.privateCardsLoading = false
      }
    },

    viewCard(card) {
      // Open read card dialog
      console.log('Viewing card:', card)
      this.openReadCardDialog(card)
    },

    showAddCardDialog() {
      this.editingCard = null
      this.preselectedCollectionId = null // Clear any pre-selected collection
      this.addCardDialogVisible = true
    },
    
    showAddCardInCollectionDialog() {
      this.editingCard = null
      // Pre-select the currently selected collection for the new card
      this.preselectedCollectionId = this.selectedCollection?.id || null
      this.addCardDialogVisible = true
    },
    
    editCard(card) {
      this.editingCard = card
      this.preselectedCollectionId = null // Clear preselection when editing
      this.addCardDialogVisible = true
    },

    async toggleCardStar(card) {
      try {
        const response = await postRequest('StarPrivateCard', {
          user_id: this.$store.state.user_id,
          card_id: card.id
        })

        if (response.data.code === 0) {
          // Update the card's star status in local data
          const cardIndex = this.privateCards.findIndex(c => c.id === card.id)
          if (cardIndex !== -1) {
            this.$set(this.privateCards[cardIndex], 'is_starred', response.data.data.is_starred)
          }

          this.$message.success(response.data.msg)
        } else {
          this.$message.error(response.data.msg || 'Failed to toggle star')
        }
      } catch (error) {
        console.error('Error toggling star:', error)
        this.$message.error('An error occurred')
      }
    },

    handleDialogClose() {
      this.addCardDialogVisible = false
      this.preselectedCollectionId = null // Clear preselection when closing
    },
    
    async handleSaveCard(cardData) {
      // Call saveCard and wait for it to complete
      const success = await this.saveCard(cardData)
      
      // Reset the EditCard's saving state regardless of success/failure
      if (this.$refs.addKnowitDialog) {
        this.$refs.addKnowitDialog.resetSavingState()
      }
      
      // Only close the dialog if save was successful
      if (success) {
        this.addCardDialogVisible = false
        
        // Clean up any lingering modal backdrops after dialog closes
        this.$nextTick(() => {
          const modals = document.querySelectorAll('.v-modal')
          modals.forEach(modal => {
            if (modal && modal.style) {
              modal.style.display = 'none'
            }
          })
        })
      }
    },

    async saveCard(cardData) {
      this.privateCardsLoading = true
      try {
        const isEditing = cardData.id && cardData.id !== ''
        const response = await postRequest('updateCard', {
          user_id: this.$store.state.user_id,
          card_data: cardData,
          is_edit: isEditing
        })

        if (response.data.code === 0) {
          this.$message.success(isEditing ? 'Card updated successfully' : 'Card created successfully')
          await this.fetchPrivateCards() // Refresh the cards list
          return true // Return success status
        } else {
          this.$message.error(response.data.msg || 'Failed to save card')
          this.privateCardsLoading = false
          return false // Return failure status
        }
      } catch (error) {
        console.error('Error saving card:', error)
        this.$message.error('Failed to save card')
        this.privateCardsLoading = false
        return false // Return failure status
      }
    },

    // Keep the old deleteCard method for KnowitCardList component (list view)
    async deleteCard(card) {
      console.log('deleteCard called with card:', card); // Debug log
      
      if (!card || !card.id) {
        console.error('Invalid card object for deletion:', card);
        this.$message.error('Cannot delete: Invalid card data');
        return;
      }
      
      this.$confirm('Are you sure you want to delete this card?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        console.log('User confirmed deletion for card ID:', card.id); // Debug log
        this.privateCardsLoading = true
        try {
          const response = await postRequest('deleteCard', {
            user_id: this.$store.state.user_id,
            card_id: card.id
          })

          if (response.data.code === 0) {
            this.$message.success('Card deleted successfully')
            await this.fetchPrivateCards() // Refresh the cards list
            await this.fetchCardCollections() // Refresh collection counts
          } else {
            this.$message.error(response.data.msg || 'Failed to delete card')
            this.privateCardsLoading = false
          }
        } catch (error) {
          console.error('Error deleting card:', error)
          this.$message.error('Failed to delete card')
          this.privateCardsLoading = false
        }
      }).catch(() => {
        console.log('User cancelled deletion'); // Debug log
        this.$message.info('Delete cancelled')
      })
    },
    
    // New method to handle successful deletion from SimpleCardView
    async handleDeleteSuccess(cardId) {
      console.log('Card deleted successfully, updating UI. Card ID:', cardId);
      
      // Remove the card from local array immediately for better UX
      const index = this.privateCards.findIndex(card => card.id === cardId);
      if (index > -1) {
        this.privateCards.splice(index, 1);
        console.log('Card removed from list. Remaining cards:', this.privateCards.length);
      } else {
        console.warn('Card not found in local array:', cardId);
      }
      
      // Only refresh collection counts, not the cards themselves
      // This avoids potential race conditions where the deleted card might still appear
      await this.fetchCardCollections(); // Refresh collection counts
      
      // Optional: You can fetch cards after a delay to ensure server has processed the deletion
      // setTimeout(() => {
      //   this.fetchPrivateCards();
      // }, 1000);
    },

    // Share card methods
    handleShareCard(card) {
      this.shareCardData = card
      this.shareForm = {
        recipient_email: '',
        message: ''
      }
      this.recipientInfo = null
      this.shareActiveTab = 'email'
      this.shareCardDialogVisible = true
      // Fetch share history when opening dialog
      this.fetchShareHistory()
    },

    async validateRecipient() {
      if (!this.shareForm.recipient_email || !this.shareForm.recipient_email.includes('@')) {
        this.recipientInfo = null
        return
      }

      try {
        const response = await postRequest('getUserByEmail', {
          email: this.shareForm.recipient_email
        })

        if (response.data.code === 0 && response.data.data) {
          this.recipientInfo = response.data.data
        } else {
          this.recipientInfo = { email: this.shareForm.recipient_email }
          this.$message.info('User not found. Card will be shared via email.')
        }
      } catch (error) {
        console.error('Error validating recipient:', error)
        this.recipientInfo = { email: this.shareForm.recipient_email }
      }
    },

    async confirmShareCard() {
      this.$refs.shareForm.validate(async (valid) => {
        if (!valid) return

        this.sharingCard = true
        try {
          // Clean card data to remove MongoDB-incompatible fields
          const cleanCardData = { ...this.shareCardData }
          // Extract ID from MongoDB format if present
          if (cleanCardData._id) {
            if (typeof cleanCardData._id === 'object') {
              cleanCardData.original_id = cleanCardData._id.$oid || cleanCardData._id.$id
              delete cleanCardData._id
            }
          }

          const response = await postRequest('shareCardToUser', {
            user_id: this.$store.state.user_id,
            card_id: this.shareCardData.id,
            recipient_email: this.shareForm.recipient_email,
            message: this.shareForm.message,
            card_data: cleanCardData // Include full card data for duplication
          })

          if (response.data.code === 0) {
            this.$message.success('Card shared successfully!')
            // Refresh share history to show the new share
            await this.fetchShareHistory()
            // Clear the form
            this.shareForm = {
              recipient_email: '',
              message: ''
            }
            this.recipientInfo = null
          } else {
            this.$message.error(response.data.msg || 'Failed to share card')
          }
        } catch (error) {
          console.error('Error sharing card:', error)
          this.$message.error('Failed to share card')
        } finally {
          this.sharingCard = false
        }
      })
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    
    async generateShareImage() {
      try {
        await this.$nextTick()

        const element = this.$refs.shareImagePreview
        if (!element) {
          throw new Error('Failed to find image preview element')
        }

        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: false,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight
        })

        return canvas
      } catch (error) {
        console.error('Error generating share image:', error)
        throw error
      }
    },

    async copyShareImage() {
      this.generatingImage = true
      try {
        const canvas = await this.generateShareImage()

        // Convert canvas to blob
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))

        // Copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ])

        this.$message.success('Image copied to clipboard!')
      } catch (error) {
        console.error('Error copying share image:', error)
        this.$message.error('Failed to copy image to clipboard')
      } finally {
        this.generatingImage = false
      }
    },

    async downloadShareImage() {
      this.generatingImage = true
      try {
        const canvas = await this.generateShareImage()
        const dataUrl = canvas.toDataURL('image/png')

        const link = document.createElement('a')
        link.download = `knowit-card-${this.shareCardData.id || 'untitled'}-${Date.now()}.png`
        link.href = dataUrl
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$message.success('Image downloaded successfully!')
      } catch (error) {
        console.error('Error downloading share image:', error)
        this.$message.error('Failed to download image')
      } finally {
        this.generatingImage = false
      }
    },
    
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return d.toLocaleDateString('en-US', options)
    },

    formatShareDate(timestamp) {
      if (!timestamp) return ''

      // Handle both Unix timestamp (number) and ISO date string
      let d
      if (typeof timestamp === 'number') {
        // Unix timestamp in seconds
        d = new Date(timestamp * 1000)
      } else {
        // ISO date string or other format
        d = new Date(timestamp)
      }

      if (isNaN(d.getTime())) return 'Invalid Date'

      const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      return d.toLocaleDateString('en-US', options)
    },
    
    getMedalInfo(cardCount) {
      if (cardCount >= 100) {
        return { name: 'Titanium Collection', icon: '💎', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
      } else if (cardCount >= 50) {
        return { name: 'Platinum Collection', icon: '🏆', color: 'linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)' }
      } else if (cardCount >= 25) {
        return { name: 'Gold Collection', icon: '🥇', color: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' }
      } else if (cardCount >= 15) {
        return { name: 'Silver Collection', icon: '🥈', color: 'linear-gradient(135deg, #e0e0e0 0%, #adadad 100%)' }
      } else if (cardCount >= 10) {
        return { name: 'Bronze Collection', icon: '🥉', color: 'linear-gradient(135deg, #ff9a56 0%, #b86f50 100%)' }
      }
      return null
    },
    
    getMedalProgress(cardCount) {
      const thresholds = [
        { threshold: 10, medal: 'Bronze Collection', color: '#ff9a56' },
        { threshold: 15, medal: 'Silver Collection', color: '#c0c0c0' },
        { threshold: 25, medal: 'Gold Collection', color: '#ffd700' },
        { threshold: 50, medal: 'Platinum Collection', color: '#a8c0ff' },
        { threshold: 100, medal: 'Titanium Collection', color: '#667eea' }
      ]
      
      for (let i = 0; i < thresholds.length; i++) {
        if (cardCount < thresholds[i].threshold) {
          const prevThreshold = i > 0 ? thresholds[i - 1].threshold : 0
          const progress = ((cardCount - prevThreshold) / (thresholds[i].threshold - prevThreshold)) * 100
          return {
            percentage: Math.min(progress, 100),
            nextMedal: thresholds[i].medal,
            nextThreshold: thresholds[i].threshold,
            color: thresholds[i].color
          }
        }
      }
      return null // Max level reached
    },

    showLoginDialog() {
      this.loginDialogVisible = true;
    },

    handleLoginSuccess() {
      this.loginDialogVisible = false;
    },

    handleAuthDialogClose(visible) {
      this.loginDialogVisible = visible
    },

    toggleAIDialog() {
      this.aiDialogVisible = !this.aiDialogVisible
    },
    
    toggleGuideAIDialog() {
      this.guideAIDialogVisible = !this.guideAIDialogVisible
    },

    handleKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'l') {
        event.preventDefault()
        this.toggleAIDialog()
      }
    },

    addToContextPot(item) {
      this.$store.dispatch('contextPot/toggleItem', item).then(result => {
        if (result.success) {
          if (result.action === 'added') {
            this.$message.success('Added to context pot');
          } else {
            this.$message.info('Removed from context pot');
          }
        }
      });
    },
    
    isCardInContextPot(card) {
      return this.globalContextPot.some(item => item.id === card.id);
    },

    showStatisticsDialog() {
      this.statisticsDialogVisible = true;
    },

    async fetchShareHistory() {
      if (!this.shareCardData || !this.shareCardData.id) return
      
      this.loadingShareHistory = true
      try {
        const response = await postRequest('getCardShareHistory', {
          user_id: this.$store.state.user_id,
          card_id: this.shareCardData.id
        })

        if (response.data.code === 0) {
          this.shareHistory = response.data.data || []
        } else {
          console.error('Failed to fetch share history:', response.data.msg)
          this.shareHistory = []
        }
      } catch (error) {
        console.error('Error fetching share history:', error)
        // Use mock data if API is not available yet
        this.shareHistory = [
          {
            recipient_email: 'john.doe@example.com',
            shared_at: new Date(Date.now() - 86400000).toISOString(),
            status: 'sent'
          },
          {
            recipient_email: 'jane.smith@example.com',
            shared_at: new Date(Date.now() - 172800000).toISOString(),
            status: 'viewed'
          }
        ]
      } finally {
        this.loadingShareHistory = false
      }
    },

    formatShareTime(timestamp) {
      if (!timestamp) return 'Unknown time'
      
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / 86400000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffMinutes = Math.floor(diffMs / 60000)

      if (diffMinutes < 1) {
        return 'Just now'
      } else if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
        })
      }
    },

    getShareStatusClass(status) {
      const statusClasses = {
        'sent': 'status-sent',
        'viewed': 'status-viewed',
        'accepted': 'status-accepted',
        'failed': 'status-failed'
      }
      return statusClasses[status] || 'status-unknown'
    },

    getShareStatusText(status) {
      const statusTexts = {
        'sent': 'Sent',
        'viewed': 'Viewed',
        'accepted': 'Accepted',
        'failed': 'Failed'
      }
      return statusTexts[status] || 'Unknown'
    },

    // Import Cards Methods
    showImportCardsDialog() {
      this.importCardsDialogVisible = true
      this.importStep = 'input'
      this.importMarkdownText = ''
      this.parsedCards = []
      this.importing = false
      this.importProgress = 0
      this.importStatusText = ''
    },

    parseMarkdownAndPreview() {
      const text = this.importMarkdownText.trim()
      if (!text) {
        this.$message.warning('Please enter some markdown content')
        return
      }

      // Parse markdown into cards
      const cards = []
      const lines = text.split('\n')
      let currentCard = null

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]

        // Check if line is a heading (# or ##)
        const headingMatch = line.match(/^#{1,2}\s+(.+)$/)

        if (headingMatch) {
          // Save previous card if exists
          if (currentCard && currentCard.title) {
            currentCard.content = currentCard.content.trim()
            cards.push(currentCard)
          }

          // Start new card
          currentCard = {
            title: headingMatch[1].trim(),
            content: ''
          }
        } else if (currentCard) {
          // Add line to current card content
          currentCard.content += line + '\n'
        }
      }

      // Don't forget the last card
      if (currentCard && currentCard.title) {
        currentCard.content = currentCard.content.trim()
        cards.push(currentCard)
      }

      if (cards.length === 0) {
        this.$message.warning('No cards found. Please make sure to use # or ## for titles.')
        return
      }

      this.parsedCards = cards
      this.importStep = 'preview'
      this.$message.success(`Successfully parsed ${cards.length} cards`)
    },

    async confirmImport() {
      if (this.parsedCards.length === 0) {
        this.$message.warning('No cards to import')
        return
      }

      if (!this.selectedCollection || this.selectedCollection === 'uncategorized') {
        this.$message.error('Please select a collection first')
        return
      }

      this.importing = true
      this.importProgress = 0
      this.importStatusText = 'Starting import...'

      const totalCards = this.parsedCards.length
      let successCount = 0
      let failCount = 0

      for (let i = 0; i < totalCards; i++) {
        const card = this.parsedCards[i]
        this.importStatusText = `Importing card ${i + 1} of ${totalCards}: ${card.title}`

        try {
          const cardData = {
            title: card.title,
            content: card.content,
            collection_ids: [this.selectedCollection.id],
            labels: [],
            resources: []
          }

          const response = await postRequest('updateCard', {
            user_id: this.$store.state.user_id,
            card_data: cardData,
            is_edit: false
          })

          if (response.data.code === 0) {
            successCount++
          } else {
            failCount++
            console.error(`Failed to import card: ${card.title}`, response.data.msg)
          }
        } catch (error) {
          failCount++
          console.error(`Error importing card: ${card.title}`, error)
        }

        this.importProgress = Math.round(((i + 1) / totalCards) * 100)
      }

      this.importStatusText = `Import complete! ${successCount} succeeded, ${failCount} failed.`

      // Refresh cards list
      await this.fetchPrivateCards()
      await this.fetchCardCollections()

      // Show success message
      if (failCount === 0) {
        this.$message.success(`Successfully imported ${successCount} cards!`)
        setTimeout(() => {
          this.importCardsDialogVisible = false
        }, 1500)
      } else {
        this.$message.warning(`Imported ${successCount} cards, ${failCount} failed.`)
      }

      this.importing = false
    },

    // Batch selection methods
    enableBatchSelection() {
      this.batchSelectionMode = true
      this.selectedCardIds = []
    },

    disableBatchSelection() {
      this.batchSelectionMode = false
      this.selectedCardIds = []
    },

    selectAll() {
      this.selectedCardIds = this.filteredCards.map(card => card.id)
    },

    deselectAll() {
      this.selectedCardIds = []
    },

    toggleCardSelection(cardId) {
      const index = this.selectedCardIds.indexOf(cardId)
      if (index > -1) {
        this.selectedCardIds.splice(index, 1)
      } else {
        this.selectedCardIds.push(cardId)
      }
    },

    isCardSelected(cardId) {
      return this.selectedCardIds.includes(cardId)
    },

    handleCardClick(card) {
      if (this.batchSelectionMode) {
        this.toggleCardSelection(card.id)
      } else {
        this.viewCard(card)
      }
    },

    // Batch operations
    async batchAddToContext() {
      const selectedCards = this.filteredCards.filter(card =>
        this.selectedCardIds.includes(card.id)
      )

      // Add all cards to context pot
      for (const card of selectedCards) {
        await this.$store.dispatch('contextPot/toggleItem', card)
      }

      this.$message.success(`Added ${selectedCards.length} cards to context`)
    },

    async batchDelete() {
      this.$confirm(
        `Are you sure you want to delete ${this.selectedCardIds.length} cards?`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(async () => {
        this.privateCardsLoading = true
        let successCount = 0
        let failCount = 0

        for (const cardId of this.selectedCardIds) {
          try {
            const response = await postRequest('deleteCard', {
              user_id: this.$store.state.user_id,
              card_id: cardId
            })

            if (response.data.code === 0) {
              successCount++
            } else {
              failCount++
            }
          } catch (error) {
            console.error('Error deleting card:', error)
            failCount++
          }
        }

        this.privateCardsLoading = false

        if (failCount === 0) {
          this.$message.success(`Successfully deleted ${successCount} cards`)
        } else {
          this.$message.warning(`Deleted ${successCount} cards, ${failCount} failed`)
        }

        // Refresh the cards list
        await this.fetchPrivateCards()
        await this.fetchCardCollections()

        // Clear selection
        this.selectedCardIds = []
        this.batchSelectionMode = false
      }).catch(() => {
        this.$message.info('Delete cancelled')
      })
    },

    async batchChangeCollections() {
      // Show the change collections dialog
      this.changeCollectionsDialogVisible = true
      this.selectedCollectionsForBatch = []
    },

    async confirmBatchChangeCollections() {
      if (this.selectedCollectionsForBatch.length === 0) {
        this.$message.warning('Please select at least one collection')
        return
      }

      const selectedCards = this.filteredCards.filter(card =>
        this.selectedCardIds.includes(card.id)
      )

      this.privateCardsLoading = true
      let successCount = 0
      let failCount = 0

      for (const card of selectedCards) {
        try {
          // Create a copy of card data and remove MongoDB _id field
          const { _id, ...cardDataWithoutId } = card

          const cardData = {
            ...cardDataWithoutId,
            collection_ids: this.selectedCollectionsForBatch
          }

          const response = await postRequest('updateCard', {
            user_id: this.$store.state.user_id,
            card_data: cardData,
            is_edit: true
          })

          if (response.data.code === 0) {
            successCount++
          } else {
            failCount++
          }
        } catch (error) {
          console.error('Error updating card:', error)
          failCount++
        }
      }

      this.privateCardsLoading = false

      if (failCount === 0) {
        this.$message.success(`Successfully updated ${successCount} cards`)
      } else {
        this.$message.warning(`Updated ${successCount} cards, ${failCount} failed`)
      }

      // Refresh the cards list
      await this.fetchPrivateCards()
      await this.fetchCardCollections()

      // Clear selection and close dialog
      this.selectedCardIds = []
      this.changeCollectionsDialogVisible = false
      this.selectedCollectionsForBatch = []
    }
  }
}
</script>

<style scoped>
.my-knowits-container {
  width: 100%;
  height: 100vh;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
}

.cards-main-container {
  display: flex;
  gap: 20px;
  flex: 1;
  overflow: hidden;
}

.collections-sidebar {
  width: 260px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
}

.collections-sidebar.collapsed {
  width: 60px;
}

.collections-sidebar.expanded {
  width: calc(100% - 40px);
  position: fixed;
  left: 20px;
  top: 80px;
  bottom: 20px;
  z-index: 100;
  max-height: calc(100vh - 100px);
}

.sidebar-controls {
  position: absolute;
  right: 8px;
  top: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.sidebar-toggle {
  width: 30px;
  height: 30px;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sidebar-toggle:hover {
  background: #f5f7fa;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.sidebar-toggle i {
  font-size: 12px;
  color: #606266;
}

.sidebar-toggle.right-arrow {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
}

.sidebar-toggle.right-arrow i {
  color: white;
}

.sidebar-toggle.right-arrow:hover {
  background: linear-gradient(135deg, #7c8ef2 0%, #8a5bb0 100%);
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 56px;
}

.sidebar-actions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
}

.collections-sidebar.collapsed .sidebar-header {
  padding: 16px 8px;
  border-bottom: none;
  min-height: 0;
}

.collections-sidebar.expanded .sidebar-header {
  padding: 24px;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.collections-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
}

.collection-item {
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 8px;
  position: relative;
}

.collections-sidebar.collapsed .collection-item {
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.collections-sidebar.collapsed .collection-item i {
  margin: 0;
  font-size: 18px;
}

.collection-item:hover {
  background-color: #f5f7fa;
}

.collection-item-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.collection-item-header {
  display: flex;
  align-items: center;
  position: relative;
}

.collection-item.active {
  background-color: #ecf5ff;
  color: #409eff;
}

.collection-item i {
  margin-right: 8px;
  font-size: 16px;
}

.collection-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 8px;
}

.collection-item .count {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
  background: #f4f4f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.collection-item.active .count {
  background: #ecf5ff;
  color: #409eff;
}

.collection-menu {
  margin-left: 8px;
  padding: 4px;
  border-radius: 4px;
  visibility: hidden;
  color: #909399;
}

.collection-item:hover .collection-menu {
  visibility: visible;
}

.collection-menu:hover {
  background: #f0f2f5;
  color: #606266;
}

/* Medal Badge Styles */
.medal-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.medal-icon {
  font-size: 14px;
  display: flex;
  align-items: center;
}

.medal-text {
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 10px;
}

/* Progress Container */
.progress-container {
  margin-top: 4px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 11px;
}

.progress-label {
  color: #606266;
  font-weight: 500;
}

.progress-count {
  color: #909399;
  font-weight: 600;
}

:deep(.progress-container .el-progress) {
  width: 100%;
}

:deep(.progress-container .el-progress-bar__outer) {
  background-color: #e4e7ed;
  border-radius: 100px;
  height: 4px !important;
}

:deep(.progress-container .el-progress-bar__inner) {
  border-radius: 100px;
  transition: all 0.3s ease;
}

.divider {
  height: 1px;
  background: #e4e7ed;
  margin: 8px 0;
}

.cards-content {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Cards Toolbar */
.cards-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e4e7ed;
  background: white;
  flex-shrink: 0;
  z-index: 10;
  gap: 16px;
}

/* Scrollable content area */
.cards-scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.selected-count {
  font-size: 14px;
  color: #606266;
  margin-left: 8px;
  font-weight: 500;
}

.operation-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: #ecf5ff;
  border-bottom: 1px solid #b3d8ff;
  flex-shrink: 0;
}

.toolbar-center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.collection-title-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.collection-title-display i {
  font-size: 16px;
}

.collection-title-display .collection-name {
  font-size: 15px;
}

.collection-title-display .collection-count {
  font-size: 13px;
  opacity: 0.9;
  margin-left: 4px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.new-card-button-wrapper {
  position: relative;
  display: inline-block;
}

.add-in-collection-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 8px;
  z-index: 20;
}

.add-in-collection-button {
  white-space: nowrap;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.view-mode-selector {
  display: flex;
  align-items: center;
}

.search-input {
  max-width: 300px;
  flex: 1;
}

:deep(.search-input .el-input__inner) {
  border-radius: 20px;
}

.cards-list {
  width: 100%;
}

.cards-list-view {
  width: 100%;
  background: white;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  margin-top: 20px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  width: 100%;
  margin-top: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.empty-state p {
  margin-bottom: 8px;
  font-size: 16px;
  color: #303133;
}

/* Share Card Dialog Styles */
.share-card-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e4e7ed;
}

.share-card-preview h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.card-preview {
  background: white;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #ebeef5;
}

.card-preview .card-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  font-size: 14px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

.card-preview .card-content {
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.recipient-info {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border-radius: 4px;
  color: #409eff;
  font-size: 13px;
}

.recipient-info i {
  margin-right: 6px;
}

/* Share History Styles */
.share-history-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.share-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.share-history-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.share-history-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 8px;
  background: #f8f9fa;
}

.share-history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.share-history-item:last-child {
  margin-bottom: 0;
}

.share-history-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.share-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.share-item-left i {
  font-size: 20px;
  color: #909399;
}

.share-item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.share-recipient {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.share-time {
  font-size: 12px;
  color: #909399;
}

.share-item-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.share-item-status.status-sent {
  background: #f0f9ff;
  color: #409eff;
}

.share-item-status.status-viewed {
  background: #f0f9ff;
  color: #67c23a;
}

.share-item-status.status-accepted {
  background: #f0f9ff;
  color: #67c23a;
  font-weight: 600;
}

.share-item-status.status-failed {
  background: #fef0f0;
  color: #f56c6c;
}

.share-history-empty,
.share-history-loading {
  text-align: center;
  padding: 32px 20px;
  color: #909399;
}

.share-history-empty i,
.share-history-loading i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
}

.share-history-empty p,
.share-history-loading p {
  margin: 0;
  font-size: 13px;
}

.share-history-loading i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Mark Button Styles */
.mark-button {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: #409eff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  color: white;
  font-size: 20px;
}

.mark-button:hover {
  background: #66b1ff;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
}

.mark-button:active {
  transform: translateY(-50%) scale(0.95);
}

/* Guide AI Button Styles */
.guide-button {
  position: fixed;
  right: 20px;
  top: calc(50% + 70px);
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: #67c23a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  color: white;
  font-size: 20px;
}

.guide-button:hover {
  background: #85ce61;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(103, 194, 58, 0.4);
}

.guide-button:active {
  transform: translateY(-50%) scale(0.95);
}

/* AI Drawer Styles */
:deep(.ai-drawer .el-drawer__body) {
  padding: 0;
  height: 100%;
}

:deep(.ai-drawer) {
  z-index: 999;
}

/* Guide AI Drawer Styles */
:deep(.guide-ai-drawer .el-drawer__body) {
  padding: 0;
  height: 100%;
}

:deep(.guide-ai-drawer) {
  z-index: 999;
}

/* Ensure drawer doesn't cover buttons */
.mark-button,
.guide-button {
  z-index: 1001;
}

/* Share Image Styles */
.share-tabs {
  margin-top: 20px;
}

.share-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
}

.share-image-preview {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  display: inline-block;
}

.share-image-card {
  width: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.share-image-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.share-image-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

.share-image-logo i {
  font-size: 18px;
}

.share-image-date {
  font-size: 12px;
  opacity: 0.9;
}

.share-image-body {
  padding: 24px 20px;
  min-height: 200px;
}

.share-image-title {
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  line-height: 1.3;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
}

.share-image-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.share-image-label {
  display: inline-block;
  padding: 4px 12px;
  background: #f0f2f5;
  border-radius: 16px;
  font-size: 12px;
  color: #606266;
  font-weight: 500;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
}

.share-image-content {
  font-size: 15px;
  line-height: 1.8;
  color: #4a4a4a;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  margin-bottom: 20px;
}

.share-image-resources {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.share-image-resources-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  font-size: 14px;
}

.share-image-resource {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  line-height: 1.5;
}

.share-image-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e4e7ed;
}

.share-image-author {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.share-image-app {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.share-image-app .app-name {
  font-size: 13px;
  color: #303133;
  font-weight: 600;
}

.share-image-app .app-domain {
  font-size: 11px;
  color: #909399;
  font-weight: 500;
}

.share-image-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

/* Grid View Styles */
.collections-grid-container {
  padding: 24px;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.grid-title {
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}

.collections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Adjust card content when sidebar is expanded */
.collections-sidebar.expanded ~ .cards-content {
  width: 300px;
  flex-shrink: 0;
}

/* Import Cards Dialog Styles */
.import-cards-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.import-step-input {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.markdown-rules-alert {
  margin-bottom: 0;
}

.markdown-rules {
  font-size: 14px;
  line-height: 1.6;
}

.markdown-rules p {
  margin: 8px 0;
}

.markdown-rules ul {
  margin: 8px 0;
  padding-left: 24px;
}

.markdown-rules li {
  margin: 4px 0;
}

.markdown-rules code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  color: #e74c3c;
}

.markdown-rules pre {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 6px;
  border-left: 3px solid #409eff;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  margin: 8px 0;
}

.import-textarea :deep(.el-textarea__inner) {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
}

.import-step-preview {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #e4e7ed;
}

.preview-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.preview-cards-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 8px;
}

.preview-card-item {
  background: white;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s;
}

.preview-card-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.preview-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.preview-card-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.preview-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  flex: 1;
}

.preview-card-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
  background: #f5f7fa;
  padding: 12px;
  border-radius: 6px;
}

.import-progress {
  margin-top: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.import-status-text {
  margin-top: 12px;
  font-size: 14px;
  color: #606266;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .posts-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(280px, calc(100% - 40px)), 1fr));
    gap: 16px;
    padding: 16px;
  }

  .left-menu {
    width: 220px;
    padding: 12px;
  }

  .collections-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .tab-container {
    flex-direction: column;
    gap: 16px;
    height: auto;
  }

  .left-menu {
    width: 100%;
    height: auto;
    max-height: 200px;
    overflow-y: auto;
  }

  .posts-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }

  .collections-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .collections-sidebar.expanded {
    width: 100%;
  }

  .collections-sidebar.expanded ~ .cards-content {
    display: none;
  }
}

/* Change Collections Dialog Styles */
.change-collections-content {
  padding: 10px 0;
}

.dialog-hint {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #606266;
}

.collection-checkbox-item {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  transition: all 0.2s;
}

.collection-checkbox-item:hover {
  background: #f5f7fa;
  border-color: #409eff;
}

.collection-checkbox-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collection-checkbox-content i {
  font-size: 16px;
  color: #909399;
}

.collection-checkbox-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.collection-checkbox-count {
  font-size: 12px;
  color: #909399;
}

.no-collections-hint {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.no-collections-hint i {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.no-collections-hint p {
  margin: 0;
  font-size: 14px;
}
</style>

<style>
/* Dialog styling for card read view (not scoped to apply to el-dialog) */
.card-read-dialog {
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.card-read-dialog .el-dialog__header {
  padding: 0;
  border-bottom: none;
}

.card-read-dialog .el-dialog__body {
  padding: 0;
  max-height: calc(90vh - 60px);
  overflow-y: auto;
  background: #fafafa;
}

.card-read-dialog .el-dialog__close {
  font-size: 24px;
  color: #606266;
}
</style>